import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ScanLine } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useCareLookup } from '@/lib/disruptions/use-disruptions';
import type { CareCardRow } from '@/lib/disruptions/types';
import { CareRedeemDialog, normalizeReference } from './care-redeem-dialog';

// Counter redemption by PRINTED reference (#808, caso 2 — passenger with no
// phone brings the paper coupon). The operator keys or scans the code; the BFF
// answers which card(s) of this disruption carry it (PNR + holder + benefit
// state) so the operator validates PNR + reference BEFORE marking. One click
// opens the same "mark consumed" dialog the rows use, with the code prefilled.
// Lookup fires on demand (Enter / button), never on keystroke.

export function CareRedeemByReference({
  disruptionUrn,
  caseUrnOf,
  onSelect,
}: {
  disruptionUrn: string;
  /** Resolves the sub-case's parent case (the redeem proxy needs it). */
  caseUrnOf: (subCaseUrn: string) => string | undefined;
  /** Pins the matched PNR into the resolve panel (same as the counter bar). */
  onSelect?: (subCaseUrn: string) => void;
}) {
  const { t } = useTranslation('care');
  const lookup = useCareLookup(disruptionUrn);
  const [query, setQuery] = useState('');
  const [redeemFor, setRedeemFor] = useState<CareCardRow | null>(null);

  const normalized = normalizeReference(query);
  const search = (): void => {
    if (normalized.length < 4 || lookup.isPending) return;
    lookup.mutate({ reference: normalized });
  };

  const result = lookup.data;
  return (
    <div className="shrink-0 rounded-lg border border-border bg-card px-3 py-2" data-testid="care-redeem-by-reference">
      <form
        className="flex items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <ScanLine className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('lookup.label')}
        </span>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('lookup.placeholder')}
          aria-label={t('lookup.label')}
          className="min-w-0 flex-1 bg-transparent font-mono text-sm uppercase tracking-[0.12em] outline-none placeholder:font-sans placeholder:normal-case placeholder:tracking-normal placeholder:text-muted-foreground"
          data-testid="care-lookup-input"
        />
        <Button type="submit" size="sm" variant="outline" disabled={normalized.length < 4 || lookup.isPending} data-testid="care-lookup-submit">
          {lookup.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" aria-hidden /> : null}
          {t('lookup.button')}
        </Button>
      </form>

      {lookup.isError ? (
        <p className="mt-1.5 text-xs text-destructive" role="alert" data-testid="care-lookup-error">
          {t('lookup.error')}
        </p>
      ) : null}

      {result ? (
        result.matches.length === 0 ? (
          <p className="mt-1.5 text-xs text-muted-foreground" data-testid="care-lookup-no-match">
            {t('lookup.noMatch', { reference: result.reference })}
          </p>
        ) : (
          <ul className="mt-1.5 space-y-1" data-testid="care-lookup-results">
            {result.matches.map((c) => {
              const redeemed = c.benefit === 'REDEEMED';
              const voided = c.benefit === 'VOIDED';
              return (
                <li
                  key={c.subCaseUrn}
                  className="flex flex-wrap items-center gap-2 rounded-md bg-muted/40 px-2.5 py-1.5 text-sm"
                  data-testid={`care-lookup-result-${c.pnrLabel}`}
                >
                  <span className="font-mono font-semibold">{c.pnrLabel}</span>
                  <span className="text-xs text-muted-foreground">{c.holderMasked}</span>
                  <span className="font-mono text-xs text-muted-foreground">{t('benefit.reference', { reference: c.reference ?? result.reference })}</span>
                  {redeemed ? (
                    <Badge size="sm" variant="neutral" data-testid="care-lookup-already">
                      {t('lookup.alreadyRedeemed')}
                    </Badge>
                  ) : voided ? (
                    <Badge size="sm" variant="error">{t('lookup.voided')}</Badge>
                  ) : null}
                  <span className="ml-auto flex items-center gap-1.5">
                    {onSelect ? (
                      <Button size="sm" variant="ghost" onClick={() => onSelect(c.subCaseUrn)} data-testid={`care-lookup-open-${c.pnrLabel}`}>
                        {t('counter.onDesk')}
                      </Button>
                    ) : null}
                    {!redeemed && !voided ? (
                      <Button size="sm" onClick={() => setRedeemFor(c)} data-testid={`care-lookup-redeem-${c.pnrLabel}`}>
                        {t('lookup.select')}
                      </Button>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        )
      ) : null}

      {redeemFor ? (
        <CareRedeemDialog
          card={redeemFor}
          caseUrn={caseUrnOf(redeemFor.subCaseUrn)}
          disruptionUrn={disruptionUrn}
          prefillReference={result?.reference ?? normalized}
          onClose={() => setRedeemFor(null)}
          onRedeemed={() => {
            setQuery('');
            lookup.reset();
          }}
        />
      ) : null}
    </div>
  );
}
